import { computed, toValue } from "vue";

/**
 * Owns the pagination state a table renders and, when `clientPaginate` is set, the slicing itself.
 *
 * The page is a controlled value: this reads it and reports the page a control asks for, and never
 * holds one of its own. That rules out VueUse's `useOffsetPagination`, which owns `currentPage`.
 *
 * @param {Object} options
 * @param {import("vue").MaybeRefOrGetter<Array>} options.items - already sorted
 * @param {import("vue").MaybeRefOrGetter<Number|String>} options.page
 * @param {import("vue").MaybeRefOrGetter<Number|String>} options.perPage
 * @param {import("vue").MaybeRefOrGetter<Number|String>} [options.total] - server total; defaults to the item count
 * @param {import("vue").MaybeRefOrGetter<Boolean>} options.clientPaginate
 * @param {import("vue").MaybeRefOrGetter<Array>} options.perPageOptions
 * @returns {Object} `{ page, perPage, pageCount, isPaginated, isFirstPage, isLastPage, visibleItems, perPageChoices }`
 */
export function useTablePagination({ items, page, perPage, total, clientPaginate, perPageOptions }) {
  const perPageCount = computed(() => toCount(toValue(perPage)));
  const requestedPage = computed(() => toCount(toValue(page)) ?? 1);

  const totalCount = computed(() => toCount(toValue(total)) ?? toValue(items).length);
  const isPaginated = computed(() => Boolean(perPageCount.value));

  const pageCount = computed(() => {
    if (!isPaginated.value) {
      return 1;
    }

    return Math.max(1, Math.ceil(totalCount.value / perPageCount.value));
  });

  // Raising the page size, or a smaller result set, can leave the controlled page past the end until
  // the consumer reacts. Everything downstream reads the clamped page, so the rows shown, the page
  // reported and the page a step asks for stay the same page.
  const pageNumber = computed(() => Math.min(Math.max(requestedPage.value, 1), pageCount.value));

  const isFirstPage = computed(() => pageNumber.value <= 1);
  const isLastPage = computed(() => pageNumber.value >= pageCount.value);

  const visibleItems = computed(() => {
    if (!isPaginated.value || !toValue(clientPaginate)) {
      return toValue(items);
    }

    const start = (pageNumber.value - 1) * perPageCount.value;

    return toValue(items).slice(start, start + perPageCount.value);
  });

  // A per-page the picker does not offer would render it blank, so the current value always appears.
  const perPageChoices = computed(() => {
    const options = toValue(perPageOptions).map(Number);

    if (options.includes(perPageCount.value)) {
      return options;
    }

    return [...options, perPageCount.value].sort((left, right) => left - right);
  });

  return {
    page: pageNumber,
    perPage: perPageCount,
    pageCount,
    isPaginated,
    isFirstPage,
    isLastPage,
    visibleItems,
    perPageChoices,
  };
}

/**
 * Returns `value` as a whole number of zero or more, or undefined when it names no such count.
 *
 * A negative `per-page` would otherwise read as paginated and slice from the end of the rows.
 *
 * A number that arrived as an attribute is a string, and the registration does not cast it, so
 * `start + perPage` would concatenate rather than add. Empty and null are undefined rather than the
 * 0 that `Number` gives them, so an absent attribute leaves pagination off instead of pinning a
 * table to page 0.
 *
 * @param {Number|String|null|undefined} value
 * @returns {Number|undefined}
 */
export function toCount(value) {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }

  return Math.floor(parsed);
}
